import { create } from "zustand";
import type {
  CNSResolvedConnection,
  CNSSourceCredentialStatus,
  CNSSourceEffectiveStatus,
} from "@/models/sourceConnection";
import { fetchResolvedSourceConnections } from "@/services/sourceConnectionService";

/** Stable empty reference for Zustand selectors (React 19 useSyncExternalStore). */
export const EMPTY_RESOLVED_CONNECTIONS: CNSResolvedConnection[] = [];
let storeGeneration = 0;

export interface SourceConnectionCachePatch {
  connectionPublicId?: string | null;
  credentialPublicId?: string | null;
  workspacePublicId?: string | null;
  projectPublicId?: string | null;
  credentialStatus?: CNSSourceCredentialStatus | null;
  effectiveStatus?: CNSSourceEffectiveStatus | null;
  enabled?: boolean | null;
  displayAccount?: string | null;
}

interface SourceConnectionStore {
  resolvedByProjectId: Record<string, CNSResolvedConnection[]>;
  loadingByProjectId: Record<string, boolean>;
  errorByProjectId: Record<string, string | null>;
  inflightByProjectId: Record<string, Promise<CNSResolvedConnection[]> | undefined>;
  fetchResolvedSourceConnections: (projectId: string) => Promise<CNSResolvedConnection[]>;
  invalidateResolved: (projectId: string) => void;
  applyConnectionPatch: (patch: SourceConnectionCachePatch) => void;
  refreshLoadedProjects: (scope?: {
    projectPublicId?: string | null;
    workspacePublicId?: string | null;
  }) => Promise<void>;
  reset: () => void;
}

export const useSourceConnectionStore = create<SourceConnectionStore>((set, get) => ({
  resolvedByProjectId: {},
  loadingByProjectId: {},
  errorByProjectId: {},
  inflightByProjectId: {},

  fetchResolvedSourceConnections: async (projectId) => {
    const inflight = get().inflightByProjectId[projectId];
    if (inflight) {
      return inflight;
    }

    const generation = storeGeneration;
    set((state) => ({
      loadingByProjectId: { ...state.loadingByProjectId, [projectId]: true },
      errorByProjectId: { ...state.errorByProjectId, [projectId]: null },
    }));

    const promise = fetchResolvedSourceConnections(projectId)
      .then((connections) => {
        if (generation === storeGeneration) {
          set((state) => ({
            resolvedByProjectId: { ...state.resolvedByProjectId, [projectId]: connections },
            loadingByProjectId: { ...state.loadingByProjectId, [projectId]: false },
          }));
        }
        return connections;
      })
      .catch((error: unknown) => {
        const message =
          error instanceof Error ? error.message : "Failed to load plugins";
        if (generation === storeGeneration) {
          set((state) => ({
            loadingByProjectId: { ...state.loadingByProjectId, [projectId]: false },
            errorByProjectId: { ...state.errorByProjectId, [projectId]: message },
          }));
        }
        throw error;
      })
      .finally(() => {
        if (generation !== storeGeneration) return;
        set((state) => {
          if (state.inflightByProjectId[projectId] !== promise) return state;
          const next = { ...state.inflightByProjectId };
          delete next[projectId];
          return { inflightByProjectId: next };
        });
      });

    if (generation === storeGeneration) {
      set((state) => ({
        inflightByProjectId: { ...state.inflightByProjectId, [projectId]: promise },
      }));
    }

    return promise;
  },

  invalidateResolved: (projectId) => {
    set((state) => {
      const resolvedByProjectId = { ...state.resolvedByProjectId };
      delete resolvedByProjectId[projectId];
      return { resolvedByProjectId };
    });
  },

  applyConnectionPatch: (patch) => {
    set((state) => {
      let changed = false;
      const resolvedByProjectId = { ...state.resolvedByProjectId };
      for (const [projectId, connections] of Object.entries(
        state.resolvedByProjectId,
      )) {
        if (patch.projectPublicId && patch.projectPublicId !== projectId) continue;
        const next = connections.map((connection) => {
          const matchesConnection =
            patch.connectionPublicId &&
            connection.connectionPublicId === patch.connectionPublicId;
          const matchesCredential =
            patch.credentialPublicId &&
            connection.credentialPublicId === patch.credentialPublicId;
          if (!matchesConnection && !matchesCredential) return connection;
          if (
            patch.workspacePublicId &&
            connection.workspacePublicId !== patch.workspacePublicId
          ) {
            return connection;
          }
          changed = true;
          return {
            ...connection,
            ...(patch.credentialStatus
              ? { credentialStatus: patch.credentialStatus }
              : {}),
            ...(patch.effectiveStatus
              ? { effectiveStatus: patch.effectiveStatus }
              : {}),
            ...(patch.enabled !== null && patch.enabled !== undefined
              ? { enabled: patch.enabled }
              : {}),
            ...(patch.displayAccount !== undefined
              ? { displayAccount: patch.displayAccount }
              : {}),
          };
        });
        resolvedByProjectId[projectId] = next;
      }
      return changed ? { resolvedByProjectId } : state;
    });
  },

  refreshLoadedProjects: async (scope) => {
    const loaded = Object.entries(get().resolvedByProjectId)
      .filter(([projectId, connections]) => {
        if (scope?.projectPublicId && projectId !== scope.projectPublicId) {
          return false;
        }
        if (scope?.projectPublicId === projectId) {
          return true;
        }
        if (
          scope?.workspacePublicId &&
          !connections.some(
            (connection) =>
              connection.workspacePublicId === scope.workspacePublicId,
          )
        ) {
          return false;
        }
        return true;
      })
      .map(([projectId]) => projectId);
    await Promise.allSettled(
      loaded.map((projectId) => get().fetchResolvedSourceConnections(projectId)),
    );
  },

  reset: () => {
    storeGeneration += 1;
    set({
      resolvedByProjectId: {},
      loadingByProjectId: {},
      errorByProjectId: {},
      inflightByProjectId: {},
    });
  },
}));

export function selectResolvedConnections(
  state: SourceConnectionStore,
  projectId: string,
): CNSResolvedConnection[] {
  return state.resolvedByProjectId[projectId] ?? EMPTY_RESOLVED_CONNECTIONS;
}
